import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Globe2,
  FileText,
  CheckCircle2,
  ArrowRight,
  Clock,
  Shield,
  MapPin,
  ArrowUpRight
} from 'lucide-react';

const CrossBorder = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeDoc, setActiveDoc] = useState(0);

  const narrativeSteps = [
    {
      title: 'Pre-Arrival Planning',
      desc: 'Before your freight approaches the border, we have already submitted PARS/PAPS and pre-cleared your shipment with CBSA and CBP.',
      icon: FileText
    },
    {
      title: 'Document Handoff',
      desc: 'Commercial invoices, packing lists, NAFTA/USMCA certificates, and B3s — prepared, verified, and submitted electronically.',
      icon: Shield
    },
    {
      title: 'Customs Clearance',
      desc: 'Our in-house brokers interface directly with CBSA and CBP. No third-party delays. Your freight clears while others are still in line.',
      icon: CheckCircle2
    },
    {
      title: 'Final Mile Delivery',
      desc: 'Once released, our ground network takes over. From border to destination — one seamless handoff, one tracking number.',
      icon: ArrowRight
    },
  ];

  const complianceData = [
    {
      side: 'Canadian',
      bg: 'bg-bone',
      textColor: 'text-forest',
      items: [
        { code: 'CBSA', desc: 'Canada Border Services Agency — customs clearance authority' },
        { code: 'CARM', desc: 'Customs Assessment and Revenue Management system' },
        { code: 'RPP', desc: 'Release Prior to Payment — expedited release program' },
        { code: 'PARS', desc: 'Pre-Arrival Review System — advance cargo reporting' },
      ]
    },
    {
      side: 'US',
      bg: 'bg-forest',
      textColor: 'text-white',
      items: [
        { code: 'CBP', desc: 'US Customs and Border Protection — primary US authority' },
        { code: 'ACE', desc: 'Automated Commercial Environment — electronic manifest' },
        { code: 'eManifest', desc: 'Advance cargo notification system for trucks' },
        { code: 'PAPS', desc: 'Pre-Arrival Processing System — US pre-clearance' },
      ]
    },
  ];

  const documents = [
    { code: 'PARS', title: 'Pre-Arrival Review System', desc: 'Advance cargo information sent to CBSA before arrival at border.' },
    { code: 'PAPS', title: 'Pre-Arrival Processing', desc: 'US CBP cargo pre-clearance system for faster border crossing.' },
    { code: 'CAD', title: 'Cargo Accompanying Document', desc: 'Required manifest document listing all cargo on the conveyance.' },
    { code: 'B3', title: 'Canada Customs Coding Form', desc: 'Detailed customs accounting document for CBSA assessment.' },
    { code: 'ACE', title: 'ACE eManifest', desc: 'Electronic manifest submission to US CBP via ACE portal.' },
  ];

  return (
    <div className="min-h-screen bg-bone">
      {/* HERO */}
      <section className="relative pt-32 pb-20 bg-forest overflow-hidden">
        <div className="absolute inset-0 bg-dots" />
        {/* Animated background orbs */}
        <motion.div 
          className="absolute top-10 right-32 w-72 h-72 bg-amber/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.35, 0.2],
            x: [0, 20, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-20 w-56 h-56 bg-white/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            y: [0, -20, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Animated Map Background */}
        <div className="absolute inset-0 opacity-20">
          <svg className="w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
            {/* US-Canada Border Line */}
            <motion.path
              d="M 200 300 Q 400 280, 600 290 T 1000 285"
              fill="none"
              stroke="#F5C518"
              strokeWidth="2"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 3, ease: "easeInOut" }}
            />
            {/* Animated Freight Dots */}
            <motion.circle
              cx="400"
              cy="290"
              r="6"
              fill="#F5C518"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0], cx: [200, 600] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            />
            <motion.circle
              cx="700"
              cy="288"
              r="6"
              fill="#F5C518"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0], cx: [1000, 600] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", delay: 2 }}
            />
          </svg>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs tracking-widest uppercase text-amber mb-4"
            >
              Cross-Border & Customs
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black text-display-lg text-white mb-6"
            >
              Erasing the 49th Parallel
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-body text-lg text-white/70 max-w-2xl"
            >
              Seamless Canada-USA freight movement with in-house customs brokerage. 
              PARS, PAPS, eManifest — we handle the complexity so your freight flows freely.
            </motion.p>
          </div>
        </div>
      </section>

      {/* NARRATIVE STEPS */}
      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">
              The Cross-Border Journey
            </div>
            <h2 className="font-display font-black text-display-lg text-forest">
              From Canada to USA, Simplified
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {narrativeSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -8 }}
                className="relative group"
              >
                <div className="bg-white p-8 rounded-2xl border border-forest/10 hover:border-amber hover:shadow-xl transition-all duration-300 h-full">
                  <motion.div 
                    className="w-12 h-12 bg-forest rounded-xl flex items-center justify-center mb-4"
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <step.icon className="w-6 h-6 text-amber" />
                  </motion.div>
                  <div className="font-mono text-xs text-forest/50 mb-2">STEP {i + 1}</div>
                  <h3 className="font-display font-bold text-xl text-forest mb-3">{step.title}</h3>
                  <p className="font-body text-ink/60 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {i < 3 && (
                  <motion.div 
                    className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.15 + 0.3 }}
                  >
                    <ArrowRight className="w-6 h-6 text-amber" />
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE SPLIT */}
      <section className="section-padding bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">
              Regulatory Expertise
            </div>
            <h2 className="font-display font-black text-display-lg text-forest">
              Dual-Side Compliance
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {complianceData.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={`${section.bg} p-8 lg:p-12 rounded-2xl`}
              >
                <div className={`font-mono text-xs tracking-widest uppercase mb-8 ${section.textColor === 'text-white' ? 'text-amber' : 'text-forest-2'}`}>
                  {section.side} Requirements
                </div>
                <div className="space-y-6">
                  {section.items.map((item, j) => (
                    <div 
                      key={j} 
                      className={`flex items-start gap-4 p-3 rounded-xl ${
                        item.code === 'PAPS' && section.bg === 'bg-forest' 
                          ? 'bg-amber/20 border-2 border-amber' 
                          : item.code === 'PAPS' 
                            ? 'bg-forest/20 border-2 border-forest' 
                            : ''
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        item.code === 'PAPS' && section.bg === 'bg-forest'
                          ? 'bg-amber text-forest'
                          : item.code === 'PAPS'
                            ? 'bg-forest text-amber'
                            : section.textColor === 'text-white' 
                              ? 'bg-amber text-steel' 
                              : 'bg-forest text-amber'
                      }`}>
                        <span className="font-mono font-bold text-sm">{item.code.slice(0, 2)}</span>
                      </div>
                      <div>
                        <div className={`font-display font-bold ${
                          item.code === 'PAPS' && section.bg === 'bg-forest'
                            ? 'text-amber'
                            : item.code === 'PAPS'
                              ? 'text-forest'
                              : section.textColor
                        }`}>
                          {item.code}
                          {item.code === 'PAPS' && (
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded font-mono ${
                              section.bg === 'bg-forest' ? 'bg-amber text-forest' : 'bg-forest text-amber'
                            }`}>
                              KEY
                            </span>
                          )}
                        </div>
                        <div className={`font-body text-sm ${section.textColor === 'text-white' ? 'text-white/60' : 'text-ink/60'}`}>
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENTS TABS */}
      <section className="section-padding bg-forest">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="font-mono text-xs tracking-widest uppercase text-amber mb-4">
              Documentation
            </div>
            <h2 className="font-display font-black text-display-lg text-white">
              Documents We Handle
            </h2>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Tab List */}
            <div className="lg:col-span-4 space-y-2">
              {documents.map((doc, i) => (
                <button
                  key={i}
                  onClick={() => setActiveDoc(i)}
                  className={`w-full text-left p-4 rounded-xl transition-all ${
                    activeDoc === i 
                      ? 'bg-amber text-forest' 
                      : 'bg-forest-2 text-white hover:bg-forest-3'
                  }`}
                >
                  <div className="font-mono font-bold text-lg">{doc.code}</div>
                  <div className={`text-sm ${activeDoc === i ? 'text-forest/70' : 'text-white/60'}`}>
                    {doc.title}
                  </div>
                </button>
              ))}
            </div>

            {/* Active Document Content */}
            <div className="lg:col-span-8">
              <motion.div
                key={activeDoc}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-forest-2 p-8 lg:p-12 rounded-2xl h-full"
              >
                <div className="w-16 h-16 bg-amber rounded-xl flex items-center justify-center mb-6">
                  <FileText className="w-8 h-8 text-forest" />
                </div>
                <div className="font-mono text-amber text-sm mb-2">{documents[activeDoc].code}</div>
                <h3 className="font-display font-bold text-2xl text-white mb-4">
                  {documents[activeDoc].title}
                </h3>
                <p className="font-body text-white/70 text-lg leading-relaxed">
                  {documents[activeDoc].desc}
                </p>
                <div className="mt-8 pt-8 border-t border-white/10">
                  <div className="flex items-center gap-4 text-white/60">
                    <Clock className="w-5 h-5" />
                    <span className="font-body text-sm">Typically processed within 2-4 hours of submission</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display font-black text-display-lg text-forest mb-6">
            Crossing Borders? Let Us Guide You.
          </h2>
          <p className="font-body text-ink/70 text-xl max-w-2xl mx-auto mb-10">
            Our customs brokerage team is standing by to ensure your freight clears smoothly 
            in both directions — Canada to USA and USA to Canada.
          </p>
          <a href="/quote" className="btn-primary text-lg px-10 py-5">
            Start Cross-Border Quote
            <ArrowUpRight className="w-6 h-6" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CrossBorder;
